/**
 * Planning for "turn a global tile's customizable switch off" (KAN-519).
 *
 * Only inheriting devices lose their customization of that exact tile. A standalone device
 * (`overrideGlobalLayout === true` on its photobooth_config doc OR on its devices doc) is never planned,
 * and a dormant `standaloneTiles` array is never read or written.
 *
 * Pure functions only, no Angular and no Firebase imports.
 */

export interface CustomizableCleanupPatch {
  /** Field-level update (Firestore dotted paths). Never contains standaloneTiles or any other field. */
  fields: Record<string, any[]>;
  /** Tile ids whose customization was removed on this device. */
  tileIds: string[];
}

/** Confirmed wording (PO, KAN-519). Single source: components must not repeat these strings. */
export const CUSTOMIZABLE_OFF_MESSAGES = {
  confirm: (n: number) =>
    `Turn off customization for this tile? Its customizations on ${n} machines that use the global layout will be removed. Standalone machines are not affected.`,
  saved: (n: number) => `Saved. Customizations removed on ${n} machines. Standalone machines were not changed.`,
  partialFailure: (k: number, ids: string[]) =>
    `Saved, but ${k} machines could not be updated: ${ids.join(', ')}. Standalone machines were not changed.`
} as const;

/** Either flag true means standalone. A missing flag on either document does not make it inheriting by itself. */
export function isStandaloneDevice(configData: any | null | undefined, deviceData: any | null | undefined): boolean {
  return configData?.['overrideGlobalLayout'] === true || deviceData?.['overrideGlobalLayout'] === true;
}

function cleanArray(arr: any, themeIds: string[], cleaned: Set<string>): any[] | null {
  if (!Array.isArray(arr)) return null;
  let changed = false;
  const next = arr.map((t: any) => {
    if (t && themeIds.includes(t.id) && t.properties?.isCustomized) {
      changed = true;
      cleaned.add(t.id);
      return { ...t, properties: { ...t.properties, isCustomized: false } };
    }
    return t;
  });
  return changed ? next : null;
}

/**
 * Computes the field-level patch for ONE inheriting device config. Returns null when the device did not
 * customize any of the given tiles (nothing to touch). Only `customizedTiles`, `layout.tiles` and `tiles`
 * are considered; every other field is left out of the patch so a merge/update keeps it byte-identical.
 */
export function planCustomizableCleanup(configData: any, themeIds: string[]): CustomizableCleanupPatch | null {
  if (!configData || !themeIds || themeIds.length === 0) return null;
  const cleaned = new Set<string>();
  const fields: Record<string, any[]> = {};

  const customized = cleanArray(configData['customizedTiles'], themeIds, cleaned);
  if (customized) fields['customizedTiles'] = customized;
  const layoutTiles = cleanArray(configData['layout']?.['tiles'], themeIds, cleaned);
  if (layoutTiles) fields['layout.tiles'] = layoutTiles;
  const tiles = cleanArray(configData['tiles'], themeIds, cleaned);
  if (tiles) fields['tiles'] = tiles;

  if (cleaned.size === 0) return null;
  return { fields, tileIds: Array.from(cleaned) };
}
